import {
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { FlexBox } from "components/flex-box";
import { useAppDispatch } from "hooks/use-app-dispatch";
import modules from "modules";
import Link from "next/link";
import { useRouter } from "next/router";
import { useEffect, useState, useMemo, useCallback } from "react";
import ProductCard1List from "./ProductCard1List";
import ArrowForward from "@mui/icons-material/ArrowForward";
import BazarImage from "components/BazarImage";
import CategorySectionHeader from "components/CategorySectionHeader";
import useResolution from "hooks/useResolution";
import LatestProductsSection from "../../../src/pages-sections/home/LatestProductsSection";
import { useBranchSelection } from "hooks/use-branch-selection";

const { actions: productsActions } = modules.products;

const ProductNotFound: React.FC = () => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { isMobile } = useResolution();

  const { selectedBranch } = useBranchSelection();

  const searchWord = useMemo(() => {
    const pathname = router?.asPath?.split("?")[0]; // Remove query string
    const segments = pathname.split("/").filter(Boolean); // Remove empty segments

    const productIndex = segments.indexOf("product");
    if (productIndex !== -1 && segments.length > productIndex + 1) {
      return segments[productIndex + 1];
    }

    return "";
  }, [router.asPath]);

  const [products, setProducts] = useState([]);
  const [latestProducts, setLatestProducts] = useState("search");

  const cleanKeyword: string = useMemo(() => {
    return searchWord?.replace(/[/\-]/g, " ") || "";
  }, [searchWord]);

  const getSearchResults = useCallback(async () => {
    if (!searchWord) return;

    try {
      const response = await dispatch(
        productsActions?.getSearchBarProductsThunk(cleanKeyword)
      ).unwrap();

      if (response.originalData.data === undefined) {
        setLatestProducts("latest");
      }

      setProducts((response?.originalData?.data || []).slice(0, 4));
    } catch (error) {
      console.error("Error fetching search results:", error);
    }
  }, [dispatch, searchWord, cleanKeyword]);

  const getLatestProducts = useCallback(async () => {
    try {
      const response = await dispatch(
        productsActions?.getLatestProductsThunk({
          branch_id: selectedBranch?.id,
        })
      ).unwrap();

      setProducts((response?.originalData?.data || []).slice(0, 4));
    } catch (error) {
      console.error("Error fetching latest products:", error);
    }
  }, [dispatch, selectedBranch?.id]);

  useEffect(() => {
    getSearchResults();
    getLatestProducts();
  }, [getLatestProducts, getSearchResults]);

  const hasProducts = useMemo(() => products.length > 0, [products]);

  return (
    <FlexBox
      flexDirection="column"
      minHeight="100vh"
      justifyContent="center"
      alignItems="center"
      px={2}
    >
      <BazarImage
        src="/assets/images/kachi/kachi-crying.png"
        sx={{
          display: "block",
          maxWidth: "180px",
          width: "100%",
        }}
      />
      <Typography my={1} variant="h4" fontWeight="bold">
        Product Not Found
      </Typography>

      <Divider sx={{ width: "100%", maxWidth: "1200px", my: "1rem" }} />

      <Grid
        container
        spacing={2}
        justifyContent="center"
        sx={{ width: "100%", maxWidth: "1200px" }}
      >
        {hasProducts && (
          <>
            <Grid item xs={12} sm={10} md={8} lg={8}>
              <CategorySectionHeader
                title={"You might like these items"}
                titleFontSize={isMobile ? "13px" : "16px"}
                seeMoreLinkTxt={products.length > 0 ? "See More" : "\u200B"}
                seeMoreLink={
                  latestProducts === "latest"
                    ? `/latest-products`
                    : `/product/search/${cleanKeyword}?limit=48&sort_direction=Lowest+to+Highes7t`
                }
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={10}
              md={8}
              lg={8}
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: isMobile ? -2 : 2,
              }}
            >
              <ProductCard1List
                products={products}
                hasProducts={hasProducts}
                isLoading={false}
              />
            </Grid>
          </>
        )}
      </Grid>

      <FlexBox flexWrap="wrap" style={{ margin: "2rem 0px 1rem 0px" }}>
        <Link href="/" passHref>
          <Button variant="contained" color="primary" sx={{ m: "0.5rem" }}>
            Back to home page
            <ArrowForward sx={{ ml: "0.5rem" }} />
          </Button>
        </Link>
      </FlexBox>
    </FlexBox>
  );
};

export default ProductNotFound;
